import { CopyOutlined, CopyTwoTone } from "@ant-design/icons";
import { Button, Flex, Tag, Typography } from "antd";
import { DrawerTable, DrawerTableColumn } from "components/UI/DrawerTable/DrawerTable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useMemo, useState } from "react";
import AceEditor from "react-ace";
import { toast } from "react-toastify";

import { ApiFieldRow, buildApiFieldRows, buildApiFieldsSchema } from "./apiFieldRows";

import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-github";

const COLUMN_WIDTHS = {
    field: 200,
    type: 90,
    required: 100,
    description: 260,
    constraints: 240,
};
const TABLE_WIDTH = Object.values(COLUMN_WIDTHS).reduce((sum, width) => sum + width, 0);

type ViewMode = "table" | "schema";

interface OfferApiFieldsTableProps {
    fields: App.Data.FormFieldData[];
}

export const OfferApiFieldsTable: FC<OfferApiFieldsTableProps> = ({ fields }) => {
    const { t } = usePaldockTranslation("drawers.offer-api-documentation");
    const { t: tC } = usePaldockTranslation("common");

    const [viewMode, setViewMode] = useState<ViewMode>("table");

    const rows = useMemo(() => buildApiFieldRows(fields), [fields]);
    const fieldsSchemaJson = useMemo(() => JSON.stringify(buildApiFieldsSchema(fields), null, 2), [fields]);

    const handleCopySchema = async () => {
        try {
            await navigator.clipboard.writeText(fieldsSchemaJson);
            toast.success(tC("copied"));
        } catch {
            toast.error(tC("copy_failed"));
        }
    };

    const columns: DrawerTableColumn<ApiFieldRow>[] = [
        {
            title: t("fields_table.columns.field"),
            dataIndex: "system_name",
            width: COLUMN_WIDTHS.field,
            render: (value: string) => (
                <Typography.Text
                    copyable={{
                        text: value,
                        icon: [<CopyOutlined key="copyOutlined" />, <CopyTwoTone key="copyTwoTone" />],
                        tooltips: [tC("copy_tooltips.click_to_copy"), tC("copy_tooltips.copied")],
                    }}
                >
                    <Typography.Text code style={{ wordBreak: "break-all" }}>
                        {value}
                    </Typography.Text>
                </Typography.Text>
            ),
        },
        {
            title: t("fields_table.columns.type"),
            dataIndex: "type",
            width: COLUMN_WIDTHS.type,
        },
        {
            title: t("fields_table.columns.required"),
            dataIndex: "is_required",
            width: COLUMN_WIDTHS.required,
            render: (value: boolean) =>
                value ? <Tag color="red">{t("fields_table.required")}</Tag> : <Tag>{t("fields_table.optional")}</Tag>,
        },
        {
            title: t("fields_table.columns.description"),
            dataIndex: "description",
            width: COLUMN_WIDTHS.description,
            render: (_: string, row: ApiFieldRow) => (
                <>
                    <div>{row.description}</div>
                    {row.note && <Typography.Text type="secondary">{row.note}</Typography.Text>}
                </>
            ),
        },
        {
            title: t("fields_table.columns.constraints"),
            dataIndex: "constraints",
            width: COLUMN_WIDTHS.constraints,
            render: (_: unknown, row: ApiFieldRow) =>
                row.constraints.map((constraint) => (
                    <div key={constraint.key}>
                        <Typography.Text type="secondary">
                            {t(`fields_table.constraints.${constraint.key}`)}:{" "}
                        </Typography.Text>
                        <Typography.Text code style={{ wordBreak: "break-all" }}>
                            {constraint.value}
                        </Typography.Text>
                    </div>
                )),
        },
    ];

    return (
        <div>
            <Flex justify="space-between" align="center" className="mb-2">
                <Typography.Title level={5} className="!mb-0">
                    {t("fields_table.title")}
                </Typography.Title>
                <Button size="small" onClick={() => setViewMode(viewMode === "table" ? "schema" : "table")}>
                    {viewMode === "table" ? t("fields_table.show_as_schema") : t("fields_table.show_as_table")}
                </Button>
            </Flex>

            {viewMode === "table" ? (
                <DrawerTable<ApiFieldRow>
                    dataSource={rows}
                    columns={columns}
                    rowKey={(row) => row.system_name}
                    scroll={{ x: TABLE_WIDTH }}
                    tableLayout="fixed"
                />
            ) : (
                <div className="relative">
                    <Button
                        icon={<CopyOutlined />}
                        size="small"
                        className="absolute top-2 right-2 z-10"
                        aria-label={tC("copy")}
                        title={tC("copy")}
                        onClick={handleCopySchema}
                    />
                    <AceEditor
                        name="api-doc-fields-schema"
                        mode="json"
                        theme="github"
                        value={fieldsSchemaJson}
                        readOnly
                        width="100%"
                        minLines={10}
                        maxLines={30}
                        wrapEnabled
                    />
                </div>
            )}
        </div>
    );
};
